import cn from 'classnames';
import { t } from 'i18n';
import React from 'react';
import { Spinner } from 'students/views/shared/components/Spinner';
import UrlIcon from 'students/views/shared/components/UrlIcon';
import styled from 'styled-components';
import pauseIcon from '../assets/pause_icon.svg';
import playIcon from '../assets/play_icon.svg';

type tSize = 'sm' | 'md' | 'lg';
export interface IProps {
  isPlaying?: boolean;
  onClick?: React.MouseEventHandler;
  disabled?: boolean;
  hasError?: boolean;
  isLoading?: boolean;
  className?: string;
  size?: tSize;
}
export const PlayButton: React.FC<IProps> = ({
  isPlaying = false,
  onClick,
  disabled,
  isLoading,
  hasError,
  className,
  size = 'lg'
}) => {
  const sizingClassName = `audio-player-size-${size}`;

  // Neither "loading" nor "error" may disable the button: only a tap can restart a load,
  // so a disabled button in either state is a permanent dead end for the student.
  const ariaLabel = (() => {
    if (isLoading) return t('frontend.media_player.audio_player.aria_loading_button');
    if (hasError) return t('frontend.media_player.audio_player.aria_reload_button');
    return isPlaying
      ? t('frontend.media_player.audio_player.aria_pause_button')
      : t('frontend.media_player.audio_player.aria_play_button');
  })();

  return (
    <SButton
      type="button"
      onClick={onClick}
      className={cn(
        { pulsing: isPlaying && !isLoading, loading: isLoading, 'has-error': hasError },
        className,
        sizingClassName
      )}
      disabled={disabled}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      {isLoading ? (
        <SSpinnerWrap>
          <Spinner />
        </SSpinnerWrap>
      ) : (
        <UrlIcon
          className="playing-btn-svg"
          url={isPlaying ? pauseIcon : playIcon}
          width="40px"
          height="40px"
        />
      )}
    </SButton>
  );
};

export default PlayButton;

const SBaseButton = styled.button`
  background: none;
  border: none;
  position: relative;
  min-width: 80px;
  width: 80px;
  height: 80px;
  border-radius: 50px;
  padding: 0;
  margin: 20px 10px;

  &:focus,
  &:hover {
    outline: none;
    box-shadow: 0 0 3px 3px var(--color-blue-btn-focus-outline-color);
    background: var(--color-blue-btn-focus-bg);
  }

  &.audio-player-size {
    &-md {
      transform: scale(0.8);
      margin: -10px;
    }
    &-sm {
      transform: scale(0.5);
      margin: -15px;
    }
  }
`;

const SButton = styled(SBaseButton)`
  &:disabled {
    &::before {
      background: #b9b9b9;
    }
    &::after {
      background: radial-gradient(circle, #ffffff 60%, #b9b9b9 65%);
    }

    .playing-btn-svg {
      color: #b9b9b9;
    }
  }

  .playing-btn-svg {
    position: absolute;
    z-index: 10;
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(-50%);
    box-sizing: content-box;
    display: block;
    width: 80px;
    height: 80px;
    border-radius: 50%;
  }

  &.pulsing {
    &::before {
      content: '';
      position: absolute;
      z-index: 0;
      left: 50%;
      top: 50%;
      transform: translateX(-50%) translateY(-50%);
      display: block;
      width: 80px;
      height: 80px;
      background: #009ee2;
      border-radius: 50%;
      animation: pulse-border 1500ms ease-out infinite;
    }
  }

  &::after {
    content: '';
    position: absolute;
    z-index: 1;
    left: 50%;
    top: 50%;
    transform: translateX(-50%) translateY(-50%);
    display: block;
    width: 80px;
    height: 80px;
    background: radial-gradient(circle, #ffffff 60%, #009ee2 65%);
    border-radius: 50%;
    transition: all 200ms;
  }

  /* Loading: solid fill behind the spinner. Still clickable — a repeated tap
     re-issues play() on the element instead of doing nothing. */
  &.loading::after {
    background: var(--color-blue-700);
  }

  /* Error: a retry affordance, deliberately not disabled. */
  &.has-error::after {
    background: radial-gradient(circle, #ffffff 60%, var(--color-red) 65%);
  }

  &.has-error .playing-btn-svg {
    color: var(--color-red);
  }

  .playing-btn:hover:after {
    background-color: darken(#fa183d, 10%);
  }

  @keyframes pulse-border {
    0% {
      transform: translateX(-50%) translateY(-50%) translateZ(0) scale(1);
      opacity: 1;
    }
    100% {
      transform: translateX(-50%) translateY(-50%) translateZ(0) scale(1.5);
      opacity: 0;
    }
  }
`;

const SSpinnerWrap = styled.span`
  position: absolute;
  z-index: 10;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  display: block;
`;
